/**
 * TextareaEditor — 素の <textarea> による**基準線**エディタ（docs/03-editor.md §1, §2）。
 *
 * この実装が守っている IME 規律:
 * 1. **非制御。** React の state に本文を持たせない。`defaultValue` で初期値を与えた後は
 *    React から value を渡さない。値は常に DOM から読む。
 * 2. **変換中に再描画しない。** `React.memo` の比較関数が常に true を返すため、親が
 *    何度描画されても textarea は触られない。値のリセットは `key` による再マウントで行う。
 * 3. **イベントは React の合成イベントを使わず、native の addEventListener で購読する。**
 *    `beforeinput` の `inputType` / `isComposing` を確実に読むため、また JSX から
 *    ハンドラ props を消して再描画要因を減らすため。
 * 4. **リスト自動継続は `keydown` ではなく `beforeinput`** で検知する（docs/03 §4-3）。
 *    `inputType` が insertLineBreak / insertParagraph、かつ `isComposing === false`、
 *    かつ composition 中でも確定直後の猶予窓でもないときだけ `continueListOnEnter()` を呼ぶ。
 *    **keyCode 229 を変換中の判定に使わない**（ADR-022）。Android Chrome は英数字入力でも
 *    229 を報告するため、これを持続状態にすると入力補助が丸ごと死ぬ。
 * 5. **Undo/Redo は独自実装しない。** 編集の適用は `setRangeText` を使い、ネイティブの
 *    undo スタックをできるだけ壊さない（docs/03 §4-7）。
 * 6. 装飾を載せない（ADR-008。フォールバックとしての単純性を優先）。
 */

import { memo, useEffect, useRef } from 'react';
import {
  continueListOnEnter,
  indentSelection as computeIndentSelection,
  type TextEdit,
  type TextState,
} from '@shared/markdown-input';
import {
  COMPOSITION_END_GRACE_MS,
  CompositionState,
  Subscribers,
  type EditorAdapter,
  type EditorViewProps,
} from './EditorAdapter';
import './editor.css';

function readState(el: HTMLTextAreaElement): TextState {
  return {
    value: el.value,
    selectionStart: el.selectionStart,
    selectionEnd: el.selectionEnd,
  };
}

/**
 * textarea 用の EditorAdapter を作る。
 * DOM への書き込みは必ずこの中を通し、composition 中は黙る。
 */
export function createTextareaAdapter(
  el: HTMLTextAreaElement,
  composition: CompositionState,
  changeSubs: Subscribers,
  compositionEndSubs: Subscribers,
): EditorAdapter {
  /** 編集を適用する。setRangeText が使えれば undo スタックを保つ。 */
  const applyEdit = (edit: TextEdit): void => {
    // IME 規律: 変換中の DOM 書き換えは行わない（docs/03 §4-2, §4-4）。
    if (composition.value) return;

    if (typeof el.setRangeText === 'function') {
      el.setRangeText(edit.insert, edit.from, edit.to, 'preserve');
    } else {
      // フォールバック（古い WebView 等）。undo スタックは失われるが本文は守る。
      const value = el.value;
      el.value = value.slice(0, edit.from) + edit.insert + value.slice(edit.to);
    }
    el.setSelectionRange(edit.selection.start, edit.selection.end);
    // setRangeText は input イベントを発火しないので、購読者へ届くよう自前で流す。
    el.dispatchEvent(new Event('input', { bubbles: true }));
  };

  return {
    getValue: () => el.value,
    setValue: (value) => {
      // IME 規律: フォーカス中・変換中の全置換は禁止（docs/03 §4-6）。
      if (composition.value) return;
      if (document.activeElement === el) return;
      el.value = value;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    },
    getSelection: () => ({ start: el.selectionStart, end: el.selectionEnd }),
    replaceRange: (from, to, text, newSelection) => {
      applyEdit({
        from,
        to,
        insert: text,
        selection: newSelection ?? { start: from + text.length, end: from + text.length },
      });
    },
    indentSelection: (dir) => {
      if (composition.value) return;
      const edit = computeIndentSelection(readState(el), dir);
      if (edit) applyEdit(edit);
    },
    isComposing: () => composition.value,
    onChange: (cb) => changeSubs.add(cb),
    onCompositionEnd: (cb) => compositionEndSubs.add(cb),
    focus: () => el.focus(),
    applyEdit,
  };
}

/**
 * 常に true を返す比較関数。
 * IME 規律: この コンポーネントは props 変化で再描画しない。差し替えは `key` で。
 */
function neverRerender(): boolean {
  return true;
}

function TextareaEditorImpl({
  initialValue,
  onReady,
  autoFocus,
  placeholder,
  className,
}: EditorViewProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  // onReady は「安定参照であること」が前提だが、念のため最新値を ref に持って
  // 依存配列を空に保つ（effect の再実行＝再マウント相当を防ぐ）。
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const composition = new CompositionState();
    const changeSubs = new Subscribers();
    const compositionEndSubs = new Subscribers();
    const adapter = createTextareaAdapter(el, composition, changeSubs, compositionEndSubs);

    const handleInput = () => {
      // 変換中でも発火する。ドラフトバッファへの退避は変換中も行うため（docs/03 §4-5）。
      changeSubs.emit();
    };
    const handleCompositionStart = () => composition.start();
    const handleCompositionEnd = () => {
      composition.end();
      compositionEndSubs.emit();
    };
    const handleKeyDown = (ev: KeyboardEvent) => {
      // 229 は「直前の keydown が IME 由来か」の一時的ヒントとしてのみ記録する。
      // 変換中かどうかの判定には使わない（ADR-022）。
      composition.observeKeydown(ev.keyCode);
    };
    const handleBlur = () => composition.reset();

    /**
     * リスト自動継続。keydown ではなく beforeinput で検知する（docs/03 §4-3）。
     * compositionend 直後の keydown で isComposing が false になるブラウザ揺れを避ける。
     */
    const handleBeforeInput = (ev: InputEvent) => {
      if (ev.inputType !== 'insertLineBreak' && ev.inputType !== 'insertParagraph') return;
      // IME 規律: 変換確定の Enter では絶対に発動しない。
      // compositionend の直後に insertParagraph が届くブラウザの揺れに備えて猶予窓も見る。
      if (ev.isComposing || composition.value || composition.endedWithin(COMPOSITION_END_GRACE_MS)) {
        return;
      }
      const edit = continueListOnEnter(readState(el));
      if (!edit) return; // 継続すべきでない → ネイティブの改行に任せる
      ev.preventDefault();
      adapter.applyEdit(edit);
    };

    el.addEventListener('input', handleInput);
    el.addEventListener('beforeinput', handleBeforeInput as EventListener);
    el.addEventListener('compositionstart', handleCompositionStart);
    el.addEventListener('compositionend', handleCompositionEnd);
    el.addEventListener('keydown', handleKeyDown);
    el.addEventListener('blur', handleBlur);

    if (autoFocus) {
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }

    onReadyRef.current(adapter);

    return () => {
      el.removeEventListener('input', handleInput);
      el.removeEventListener('beforeinput', handleBeforeInput as EventListener);
      el.removeEventListener('compositionstart', handleCompositionStart);
      el.removeEventListener('compositionend', handleCompositionEnd);
      el.removeEventListener('keydown', handleKeyDown);
      el.removeEventListener('blur', handleBlur);
      changeSubs.clear();
      compositionEndSubs.clear();
      onReadyRef.current(null);
    };
    // 依存配列は空。autoFocus 等の変化で再実行しない（再マウントは key で行う）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <textarea
      ref={ref}
      className={className ? `mm-editor-textarea ${className}` : 'mm-editor-textarea'}
      defaultValue={initialValue}
      placeholder={placeholder}
      spellCheck={false}
      autoCapitalize="off"
      autoCorrect="off"
      // IME 規律: value / onChange を渡さない（非制御）。React に再描画の口実を与えない。
    />
  );
}

/** IME 規律: props 変化では再描画しない。内容の差し替えは `key` による再マウントで。 */
export const TextareaEditor = memo(TextareaEditorImpl, neverRerender);
TextareaEditor.displayName = 'TextareaEditor';
